import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import api from '../../services';
import { readTenantBranding, isBrandingConfigured } from '../../utils/tenantBrandingCache';
import './AccountBanners.css';

const DAY_MS = 24 * 60 * 60 * 1000;
// Rough per-document time savings vs manual paperwork; used for the trial value line.
const MINUTES_SAVED_PER_QUOTATION = 25;
const MINUTES_SAVED_PER_JOB = 15;

/** Paths where banners would be noise (settings itself, onboarding, public flows, checkout page itself). */
const SUPPRESS_PATHS = ['/onboarding', '/account-locked', '/subscribe'];

function formatHoursSaved(jobs, quotations) {
  const minutes = jobs * MINUTES_SAVED_PER_JOB + quotations * MINUTES_SAVED_PER_QUOTATION;
  if (minutes < 60) return null;
  const hours = Math.round(minutes / 60);
  return hours;
}

/**
 * Persistent account-level strips under the header:
 *  1. Branding setup reminder — bold, until the tenant saves real branding.
 *  2. Trial countdown — days left, value recap, upgrade CTA. Typical-SaaS behavior.
 */
export default function AccountBanners() {
  const authUser = useSelector((state) => state.Auth.authUser);
  const location = useLocation();
  const [branding, setBranding] = useState(() => readTenantBranding());
  const [sub, setSub] = useState(null);

  useEffect(() => {
    if (!authUser) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/tenant/settings');
        if (!cancelled && data) setBranding(data);
      } catch (_) {
        /* keep cached value */
      }
      try {
        const { data } = await api.get('/tenant/subscription');
        if (!cancelled && data) setSub(data);
      } catch (_) {
        /* non-blocking */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authUser]);

  if (!authUser) return null;
  if (SUPPRESS_PATHS.some((p) => location.pathname.startsWith(p))) return null;

  const showBranding = !isBrandingConfigured(branding);

  const subscription = sub && sub.subscription;
  const suspended = sub && sub.tenantStatus === 'suspended';
  const trialing =
    !suspended && subscription && subscription.status === 'trialing' && subscription.trialEndsAt;
  const pastDue = !suspended && subscription && subscription.status === 'past_due';
  let daysLeft = null;
  if (trialing) {
    daysLeft = Math.max(0, Math.ceil((new Date(subscription.trialEndsAt) - Date.now()) / DAY_MS));
  }
  const usage = (sub && sub.usage) || {};
  const hoursSaved = formatHoursSaved(usage.jobsCreated || 0, usage.quotationsCreated || 0);

  return (
    <>
      {showBranding && (
        <div className="account-banner account-banner--branding" role="region" aria-label="Branding setup reminder">
          <span className="account-banner__text">
            <strong>Set up your branding.</strong>{' '}
            Your quotes and documents currently show placeholder details ("ABC Company", "XYZ
            Address") and a default logo. Add your company name, logo, and address so customers see
            your brand.
          </span>
          <Link to="/settings-profile?tab=branding" className="account-banner__cta">
            Set up branding
          </Link>
        </div>
      )}
      {suspended && (
        <div
          className="account-banner account-banner--trial account-banner--urgent"
          role="region"
          aria-label="Account suspended"
        >
          <span className="account-banner__text">
            <strong>Your free trial has ended and your account is paused.</strong>{' '}
            Your data is untouched and nothing was charged — pick a plan to reactivate.
          </span>
          <Link to="/subscribe" className="account-banner__cta">
            Choose a plan
          </Link>
        </div>
      )}
      {pastDue && (
        <div
          className="account-banner account-banner--pastdue"
          role="region"
          aria-label="Payment failed"
        >
          <span className="account-banner__text">
            <strong>Your last payment failed.</strong>{' '}
            Update your payment method to avoid losing access when your grace period ends.
          </span>
          <Link
            to={`/subscribe?plan=${encodeURIComponent(sub?.plan?.key || '')}&seats=${encodeURIComponent(subscription?.seatLimit || '')}`}
            className="account-banner__cta"
          >
            Update payment method
          </Link>
        </div>
      )}
      {trialing && (
        <div
          className={`account-banner account-banner--trial${daysLeft <= 2 ? ' account-banner--urgent' : ''}`}
          role="region"
          aria-label="Trial status"
        >
          <span className="account-banner__text">
            <strong>
              {daysLeft === 0
                ? 'Your free trial ends today.'
                : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left in your free trial.`}
            </strong>{' '}
            {hoursSaved
              ? `You've saved roughly ${hoursSaved} hour${hoursSaved === 1 ? '' : 's'} of paperwork with ${usage.jobsCreated || 0} jobs and ${usage.quotationsCreated || 0} quotations so far.`
              : 'Keep your jobs, quotations, and branding — pick a plan before the trial ends.'}
          </span>
          <Link to="/subscribe" className="account-banner__cta">
            Start subscription
          </Link>
        </div>
      )}
    </>
  );
}
